// GlassSkeleton: shimmering placeholder blocks used while data is
// still fetching. Kept dependency-free so any page can import it.
// Every element sits inside the .skeleton-shimmer CSS animation which
// runs on the compositor thread thanks to translateX + will-change,
// so the shimmer is smooth even on modest hardware.
import React from "react";

export function SkeletonLine({ w = "100%", h = 14, className = "" }) {
  return (
    <div
      className={`skeleton-shimmer ${className}`}
      style={{ width: w, height: h, borderRadius: 6 }}
      aria-hidden="true"
    />
  );
}

export function SkeletonCard({ lines = 4, className = "" }) {
  return (
    <div className={`glass-card rounded-2xl p-5 space-y-3 ${className}`} aria-busy="true">
      <SkeletonLine w="55%" h={12} />
      <SkeletonLine w="85%" h={26} />
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonLine key={i} w={`${65 + ((i * 17) % 30)}%`} h={12} />
      ))}
    </div>
  );
}

export function SkeletonStatTile() {
  return (
    <div className="glass-card rounded-2xl p-5 space-y-3" aria-busy="true">
      <SkeletonLine w="40%" h={11} />
      <SkeletonLine w="70%" h={28} />
      <SkeletonLine w="55%" h={10} />
    </div>
  );
}

export function SkeletonTableRow({ cols = 5 }) {
  return (
    <tr aria-busy="true">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <SkeletonLine w={`${60 + ((i * 13) % 30)}%`} h={12} />
        </td>
      ))}
    </tr>
  );
}

// Full dashboard skeleton: mirrors the real Dashboard layout so nothing
// visibly shifts once data loads.
export function DashboardSkeleton() {
  return (
    <div className="space-y-8" aria-busy="true" data-testid="dashboard-skeleton">
      <div className="space-y-2">
        <SkeletonLine w={160} h={12} />
        <SkeletonLine w={380} h={38} />
        <SkeletonLine w={520} h={14} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonStatTile key={i} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SkeletonCard lines={5} className="lg:col-span-2" />
        <SkeletonCard lines={4} />
      </div>
    </div>
  );
}
